// ---- her place ----
// A table on the grass — a low carved one, the kind a sofra is laid on — and a
// few trees far enough out to be scenery. No pond — she never calls setPond,
// and the engine's water checks answer "nowhere is wet" to every question,
// which is exactly a field of dry grass (core/water.ts).
import * as THREE from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {asset} from '../../core/assets';
import {LAMB,mesh,scene,trees} from '../../core/scene';
import {isMesh} from '../../core/body';
import {pack} from './pack';

// The table: where she stands to work and where the food goes. It is a
// RECTANGLE, `hx` by `hz` either side of its middle, long side across the
// picture — the serve draws its spot over that top (field.ts), he stands at its
// edge to eat (boy.ts) — and `h` is how high the top sits, which is where a
// dish lands (dishes.ts). Same middle as the round one it replaced, so the
// camera's pivot still holds; and everything that keeps a person off it now
// measures from its EDGE (edge, below) rather than from its middle, which is
// the only way "a stride off the table" means the same thing along a long side
// and at a corner.
//
// It is LOW on purpose. A grown-up's dining table — 0.75 m, about 1.05 units
// against her 2.25 — would come up to his shoulders, and a boy whose chin is
// on the table is not one who can eat off it. At 0.72 it is his waist and her
// mid-thigh, and it sits under the camera's pivot (T.y 1.05, game.ts), so the
// top is seen from above and the food on it shows, rather than edge-on as a
// line with plates floating over it. The model is a coffee table of about this
// height and two-thirds this size, so it is drawn wider than it was made
// (loadTable, below): a low table with room for a spread on it, rather than a
// table at the right height with room for four.
export const SOFRA={x:0,z:1.2,hx:1.6,hz:0.95,h:0.72};
// How far (x,z) is outside the top's edge, seen from above, and negative over
// the top itself. Every "keep off the table" in the three brains is this
// against a number, where it used to be the distance from the middle against
// the radius plus that number.
export function edge(x:number,z:number){
  const qx=Math.abs(x-SOFRA.x)-SOFRA.hx, qz=Math.abs(z-SOFRA.z)-SOFRA.hz;
  return Math.hypot(Math.max(qx,0),Math.max(qz,0))+Math.min(Math.max(qx,qz),0);
}
// ...and the way straight out from it at (x,z): off the nearest side, or off
// the corner, as a unit. What a push off the table goes along, and what a step
// that would go into it slides across (stepTo, boy.ts).
export function outward(x:number,z:number){
  const dx=x-SOFRA.x, dz=z-SOFRA.z, qx=Math.abs(dx)-SOFRA.hx, qz=Math.abs(dz)-SOFRA.hz;
  const sx=dx<0?-1:1, sz=dz<0?-1:1;
  if(qx>0&&qz>0){const l=Math.hypot(qx,qz);return {x:sx*qx/l,z:sz*qz/l};}
  return qx>qz?{x:sx,z:0}:{x:0,z:sz};
}
// The point `off` out from the edge on the bearing `a` from the middle — the
// bearing being atan2(x,z), as every heading in the house is. Where a ring
// about the table used to be the middle plus a radius, it is this: her orbit,
// his ring and his father's are all distances from the wood, so they are as
// far off the long side as off the corner.
export function ring(a:number,off:number){
  const s=Math.sin(a), c=Math.cos(a), as=Math.abs(s), ac=Math.abs(c), {hx,hz}=SOFRA;
  let t:number;
  if(as>1e-9&&ac*(hx+off)/as<=hz)t=(hx+off)/as;          // out through an end
  else if(ac>1e-9&&as*(hz+off)/ac<=hx)t=(hz+off)/ac;     // out through a long side
  else{const B=hx*as+hz*ac;t=B+Math.sqrt(Math.max(0,B*B-(hx*hx+hz*hz-off*off)));}   // round a corner
  return {x:SOFRA.x+s*t,z:SOFRA.z+c*t};
}
// How near the table's EDGE a straight walk from (px,pz) to (qx,qz) ever comes:
// negative if it crosses the top. Both ends of a stroll can sit out on a ring
// while the line between them runs right over the sofra, so every leg the
// three of them walk is checked by this, and not just where it ends. The
// distance from a box is convex, so along a straight line it has one low point
// and a ternary search finds it.
export function legClears(px:number,pz:number,qx:number,qz:number){
  let a=0, b=1;
  const at=(k:number)=>edge(px+(qx-px)*k,pz+(qz-pz)*k);
  for(let i=0;i<40;i++){const m1=a+(b-a)/3, m2=b-(b-a)/3; if(at(m1)<at(m2))b=m2; else a=m1;}
  return Math.min(at((a+b)/2),at(0),at(1));
}
// ---- the way round ----
// He goes ROUND the table to get to his food (boy.ts), and round a rectangle is
// not a matter of angles: a tenth of a turn is a stride along an end and two
// along a side. So the way round is measured as a walk, by distance, along the
// line `off` out from the edge — the long sides, the ends, and a quarter circle
// at each corner — starting at the middle of the side facing +z and going the
// way the bearing grows. rimAt is the point a distance `s` along it; rimS the
// distance along it of the point on it nearest (x,z).
export const rimLen=(off:number)=>4*(SOFRA.hx+SOFRA.hz)+2*Math.PI*off;
const CORNERS=():[number,number][]=>[[SOFRA.hx,SOFRA.hz],[SOFRA.hx,-SOFRA.hz],[-SOFRA.hx,-SOFRA.hz],[-SOFRA.hx,SOFRA.hz]];
export function rimAt(s:number,off:number){
  const {hx}=SOFRA, q=Math.PI/2*off, L=rimLen(off), C=CORNERS();
  let u=((s%L)+L)%L;
  const at=(x:number,z:number)=>({x:SOFRA.x+x,z:SOFRA.z+z});
  if(u<hx)return at(u,SOFRA.hz+off);                       // the first half of the near-z side
  u-=hx;
  for(let k=0;k<4;k++){
    const [cx,cz]=C[k];
    if(u<q){const th=k*Math.PI/2+u/off;return at(cx+off*Math.sin(th),cz+off*Math.cos(th));}
    u-=q;
    const [nx,nz]=C[(k+1)%4], len=k<3?Math.hypot(nx-cx,nz-cz):hx, th=(k+1)*Math.PI/2;
    if(u<len||k===3)return at(cx+Math.sign(nx-cx)*u+off*Math.sin(th),cz+Math.sign(nz-cz)*u+off*Math.cos(th));
    u-=len;
  }
  return at(0,SOFRA.hz+off);
}
export function rimS(x:number,z:number,off:number){
  const {hx,hz}=SOFRA, q=Math.PI/2*off, dx=x-SOFRA.x, dz=z-SOFRA.z, qx=Math.abs(dx)-hx, qz=Math.abs(dz)-hz;
  // where each corner's arc starts along the way round
  const S=[hx, hx+q+2*hz, 3*hx+2*q+2*hz, 3*hx+3*q+4*hz];
  if(qx>0&&qz>0){                                          // off a corner: how far round its arc
    const k=dx>0?(dz>0?0:1):(dz>0?3:2), th=Math.atan2(qx*Math.sign(dx),qz*Math.sign(dz));
    const a=((th-k*Math.PI/2)%(2*Math.PI)+2*Math.PI)%(2*Math.PI);
    return S[k]+off*Math.min(a,Math.PI/2);
  }
  if(qx>qz)return dx>0?S[0]+q+(hz-dz):S[2]+q+(dz+hz);       // an end
  return dz>0?(dx>=0?dx:S[3]+q+(dx+hx)):S[1]+q+(hx-dx);     // a long side
}
// The shorter signed way from one distance round to another
export const rimWrap=(d:number,off:number)=>{const L=rimLen(off);return ((d%L)+L*1.5)%L-L/2;};

// ---- the table itself ----
// A carved wooden table — turned legs, a fretwork apron, a plank top. It arrives
// as a model (the pack says where from, TABLE in pack.ts), and it is drawn to
// fill SOFRA exactly: top to edge, foot to grass, whatever shape it was made
// in. Until it arrives there is nothing there, which is the bargain the three
// people have too.
const table=new THREE.Group(); table.position.set(SOFRA.x,0,SOFRA.z); scene.add(table);
export function loadTable(){
  const file=pack().TABLE;
  new GLTFLoader().loadAsync(asset(file)).then(g=>{
    const m=g.scene; m.updateMatrixWorld(true);
    const box=new THREE.Box3().setFromObject(m), sz=box.getSize(new THREE.Vector3()), mid=box.getCenter(new THREE.Vector3());
    m.scale.set(2*SOFRA.hx/sz.x,SOFRA.h/sz.y,2*SOFRA.hz/sz.z);
    m.position.set(-mid.x*m.scale.x,-box.min.y*m.scale.y,-mid.z*m.scale.z);
    m.traverse(o=>{if(isMesh(o)){o.castShadow=true;o.receiveShadow=true;}});
    table.add(m);
  }).catch(err=>console.warn(file+' did not load',err));
}

// Far enough out to be horizon rather than obstacle.
trees([[-11,-7.5],[-4,-12.5],[6.5,-13],[12,-6],[12.5,1.5],[-11.5,1]],0x5FAE43);
// a low wall of her house behind, hazed in the fog
const WALL=LAMB(0xF6EFE0);
mesh(new THREE.BoxGeometry(7,2.4,2.5),WALL,-2,1.2,-23);
for(const x of [-5.6,1.6])mesh(new THREE.BoxGeometry(1.6,3.4,2.6),WALL,x,1.7,-23);
